import { useState, useMemo } from 'react'
import { X, Search } from 'lucide-react'
import { ImageGridItem } from './ImageGridItem'
import { ImageCategoryFilter } from './ImageCategoryFilter'
import type { AvatarCategory } from '../../config/avatarLibrary'
import { getAvatarsByCategory, searchAvatars, getAvatarUrl } from '../../config/avatarLibrary'

interface ImageLibraryModalProps {
    isOpen: boolean
    onClose: () => void
    onSelectImage: (imageUrl: string) => void
    currentImage?: string | null
}

export function ImageLibraryModal({ isOpen, onClose, onSelectImage, currentImage }: ImageLibraryModalProps) {
    const [activeCategory, setActiveCategory] = useState<AvatarCategory>('all')
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(currentImage || null)

    // Filter images based on category and search
    const filteredImages = useMemo(() => {
        let images = getAvatarsByCategory(activeCategory)

        if (searchQuery.trim()) {
            const searchResults = searchAvatars(searchQuery)
            // Intersect category filter with search results
            images = images.filter(img =>
                searchResults.some(result => result.id === img.id)
            )
        }

        return images
    }, [activeCategory, searchQuery])

    if (!isOpen) return null

    const handleConfirm = () => {
        if (selectedImageUrl) {
            onSelectImage(selectedImageUrl)
        }
    }

    const handleClose = () => {
        setSearchQuery('')
        setActiveCategory('all')
        onClose()
    }

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={handleClose}
        >
            <div
                className="relative w-full max-w-4xl max-h-[90vh] bg-white dark:bg-black rounded-2xl shadow-2xl overflow-hidden flex flex-col m-4"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-black/80 backdrop-blur-md">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        Biblioteca de Imagens
                    </h2>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-900 rounded-full transition-colors"
                        aria-label="Fechar"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Search Bar */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Buscar por tags (ex: formal, casual, azul...)"
                            className="
                                w-full pl-10 pr-4 py-2.5 
                                bg-gray-50 dark:bg-gray-900 
                                border border-gray-200 dark:border-gray-800 
                                rounded-lg
                                text-gray-900 dark:text-white
                                placeholder-gray-400
                                focus:outline-none focus:ring-2 focus:ring-blue-500
                                transition-colors
                            "
                        />
                    </div>
                </div>

                {/* Category Filter */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                    <ImageCategoryFilter
                        activeCategory={activeCategory}
                        onCategoryChange={setActiveCategory}
                    />
                </div>

                {/* Image Grid */}
                <div className="flex-1 overflow-y-auto p-6">
                    {filteredImages.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {filteredImages.map((avatar) => {
                                const imageUrl = getAvatarUrl(avatar.filename)
                                return (
                                    <ImageGridItem
                                        key={avatar.id}
                                        imageUrl={imageUrl}
                                        isSelected={selectedImageUrl === imageUrl}
                                        onSelect={() => setSelectedImageUrl(imageUrl)}
                                        alt={`Avatar ${avatar.id}`}
                                    />
                                )
                            })}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                                <Search className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">
                                Nenhuma imagem encontrada
                            </p>
                            <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
                                Tente ajustar os filtros ou a busca
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {selectedImageUrl ? 'Imagem selecionada' : 'Selecione uma imagem'}
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={handleClose}
                                className="
                                    px-4 py-2 rounded-lg
                                    text-gray-700 dark:text-gray-300
                                    hover:bg-gray-200 dark:hover:bg-gray-800
                                    transition-colors
                                    font-medium
                                "
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleConfirm}
                                disabled={!selectedImageUrl}
                                className="
                                    px-6 py-2 rounded-lg
                                    bg-blue-500 text-white
                                    hover:bg-blue-600
                                    disabled:opacity-50 disabled:cursor-not-allowed
                                    transition-colors
                                    font-medium
                                "
                            >
                                Confirmar Seleção
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
